import { ApiIntegrationCreateAndPublishPingPaymentAgreementPostRequest, ApiIntegrationCreatePaymentLinkPostRequest, IntegrationApi, WebApiIntegrationsDtoConnectIntegrationDto, WebApiIntegrationsDtoCreateAgreementDto, WebApiIntegrationsDtoCreateMultiplePaymentLinkDto, WebApiIntegrationsDtoCreatePaymentLinkDto, WebApiIntegrationsDtoIntegrationSyncConfigDto, WebApiIntegrationsDtoRefundPaymentLinkDto } from "../Repository/eventbookyapi";
import { IntegrationType } from "../Types/IntegrationType";
import { GetApiConfiguration } from "./ServiceConfiguration";

export abstract class IntegrationService {
    public static GetIntegration = async (type: IntegrationType) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGet({ type });
    };

    public static GetActiveIntegrations = async () => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetActiveIntegrationsGet();
    };

    public static GetInvoices = async (type: IntegrationType) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetInvoicesGet({ type });
    };

    public static GetMemberSyncReport = async (integrationType: IntegrationType) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetMemberSyncReportGet({ integrationType });
    };

    public static CreatePaymentLink = async (request: WebApiIntegrationsDtoCreatePaymentLinkDto) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCreatePaymentLinkPost({ webApiIntegrationsDtoCreatePaymentLinkDto: request });
    };

    public static CreateMultiplePaymentLinks = async (request: WebApiIntegrationsDtoCreateMultiplePaymentLinkDto) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCreateMultiplePaymentLinksPost({ webApiIntegrationsDtoCreateMultiplePaymentLinkDto: request });
    };

    public static CancelPaymentLink = async (paymentUrl: string) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCancelPaymentLinkDelete({ paymentUrl });
    };

    public static CreateInvoiceFromPaymentLink = async (paymentLinkId: string) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCreateInvoiceFromPaymentLinkPut({ paymentLinkId });
    };

    public static GetInvoiceFromPaymentLink = async (paymentLinkId: string) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetInvoiceFromPaymentLinkGet({ paymentLinkId });
    };

    public static DownloadInvoiceFromPaymentLink = async (paymentLinkId: string) => {
        const response = await new IntegrationApi(GetApiConfiguration()).apiIntegrationDownloadInvoiceFromPaymentLinkGetRaw({ paymentLinkId });
        return {
            blob: await response.raw.blob(),
            contentDisposition: response.raw.headers.get('content-disposition')
        };
    };

    public static GetReceiptFromPaymentLink = async (paymentLinkId: string) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetReceiptFromPaymentLinkGet({ paymentLinkId });
    };

    public static DownloadReceiptFromPaymentLink = async (paymentLinkId: string) => {
        const response = await new IntegrationApi(GetApiConfiguration()).apiIntegrationGetReceiptFromPaymentLinkGetRaw({ paymentLinkId });
        return {
            blob: await response.raw.blob(),
            contentDisposition: response.raw.headers.get('content-disposition')
        };
    };

    public static RemoveIntegration = async (id: number) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationDelete({ integrationId: id });
    };

    public static GetInvoicePdf = async (type: IntegrationType, invoiceNumber: string) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetInvoicePdfGetRaw({ type, invoiceNumber })
    }

    public static ConnectIntegration = async (dto: WebApiIntegrationsDtoConnectIntegrationDto) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationPost({
            webApiIntegrationsDtoConnectIntegrationDto: dto
        });
    };

    public static CreateAndPublishPingPaymentAgreement = async (dto: WebApiIntegrationsDtoCreateAgreementDto) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCreateAndPublishPingPaymentAgreementPost({
            webApiIntegrationsDtoCreateAgreementDto: dto
        });
    };

    public static RefundPaymentLink = async (dto: WebApiIntegrationsDtoRefundPaymentLinkDto) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationRefundPaymentLinkPost({
            webApiIntegrationsDtoRefundPaymentLinkDto: dto
        });
    };

    public static GetRefundsByMember = async (memberId: number) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetRefundsByMemberGet({ memberId });
    };

    public static GetAllocations = async (fromDate?: string, toDate?: string) => {
        const url = new URL(`${process.env.REACT_APP_API_URL_BASE}/api/Integration/GetAllocations`);
        if (fromDate) url.searchParams.set('fromDate', fromDate);
        if (toDate) url.searchParams.set('toDate', toDate);
        const response = await fetch(url.toString(), { credentials: 'include' });
        return response.json();
    };

    public static DownloadAllocationsPdf = async (fromDate?: string, toDate?: string) => {
        const url = new URL(`${process.env.REACT_APP_API_URL_BASE}/api/Integration/GetAllocationsPdf`);
        if (fromDate) url.searchParams.set('fromDate', fromDate);
        if (toDate) url.searchParams.set('toDate', toDate);
        const response = await fetch(url.toString(), { credentials: 'include' });
        return response.blob();
    };

    public static GetFortnoxSyncConfig = async (): Promise<WebApiIntegrationsDtoIntegrationSyncConfigDto> => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetFortnoxSyncConfigGet();
    };

    public static UpdateFortnoxSyncConfig = async (dto: WebApiIntegrationsDtoIntegrationSyncConfigDto): Promise<void> => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationUpdateFortnoxSyncConfigPut({
            webApiIntegrationsDtoIntegrationSyncConfigDto: dto
        });
    };

    public static SyncFullIntegration = async (integrationType: IntegrationType): Promise<void> => {
        if (integrationType === IntegrationType.Spiris) {
            return new IntegrationApi(GetApiConfiguration()).apiIntegrationSyncSpirisDataForCurrentCompanyPost();
        }
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationSyncFortNoxDataForCurrentCompanyPost();
    };

    public static GetIntegrationSyncConfig = async (integrationType: IntegrationType): Promise<WebApiIntegrationsDtoIntegrationSyncConfigDto> => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationGetIntegrationSyncConfigGet({ integrationType });
    };

    public static UpdateIntegrationSyncConfig = async (dto: WebApiIntegrationsDtoIntegrationSyncConfigDto): Promise<void> => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationUpdateIntegrationSyncConfigPut({
            webApiIntegrationsDtoIntegrationSyncConfigDto: dto
        });
    };

    public static SyncMembersToFortnox = async () => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationSyncMembersToFortnoxPost();
    };

    public static CreateMemberInFortnox = async (memberId: number) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCreateMemberInFortnoxPost({ memberId });
    };

    public static SyncMembersToIntegration = async (integrationType: IntegrationType) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationSyncMembersToIntegrationPost({ integrationType });
    };

    public static CreateMemberInIntegration = async (memberId: number, integrationType: IntegrationType) => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationCreateMemberInIntegrationPost({ memberId, integrationType });
    };

    public static LinkMemberToIntegration = async (memberId: number, integrationType: IntegrationType): Promise<void> => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationLinkMemberToIntegrationPost({ memberId, integrationType });
    };

    public static CheckFortnoxCustomer = async (customerNumber: string): Promise<string | null> => {
        try {
            return await new IntegrationApi(GetApiConfiguration()).apiIntegrationCheckFortnoxCustomerGet({ customerNumber });
        } catch {
            return null;
        }
    };

    public static CheckSpirisCustomer = async (customerNumber: string): Promise<string | null> => {
        try {
            return await new IntegrationApi(GetApiConfiguration()).apiIntegrationCheckSpirisCustomerGet({ customerNumber });
        } catch {
            return null;
        }
    };

    public static LinkMemberToFortnox = async (memberId: number): Promise<void> => {
        return new IntegrationApi(GetApiConfiguration()).apiIntegrationLinkMemberToFortnoxPost({ memberId });
    };
};